// useKyrsTasks — every listed KYRS leaf task, resolved to its SHAPE sector, for
// the Complete % pool (sectorProgress.computeComplete). Sector = tasks.sector,
// else the list's default sector (task_lists.olmps_sector). A task with subtasks
// never counts itself — its subtasks do. One load per (user, data version),
// shared by Home, the compass and the sector overview so all three match.
import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useDataVersion } from '../live/screenContext';
import type { Sector } from '../domain/olmps';
import type { KyrsLeafTask } from './sectorProgress';

const asSector = (v: unknown): Sector | null => (typeof v === 'string' && v.length === 1 && 'SHAPE'.includes(v) ? (v as Sector) : null);
const inflight = new Map<string, Promise<KyrsLeafTask[]>>();

function load(userId: string, version: number): Promise<KyrsLeafTask[]> {
  const k = `${userId}:${version}`;
  const hit = inflight.get(k);
  if (hit) return hit;
  const p = (async () => {
    const [t, l] = await Promise.all([
      supabase.from('tasks').select('id,done,unlisted,sector,list_id,parent_task_id').eq('user_id', userId).limit(5000),
      supabase.from('task_lists').select('id,olmps_sector').eq('user_id', userId),
    ]);
    const listSector = new Map<string, Sector>();
    for (const r of (l.data ?? []) as { id: string; olmps_sector: string | null }[]) {
      const s = asSector(r.olmps_sector);
      if (s) listSector.set(r.id, s);
    }
    const rows = (t.data ?? []) as { id: string; done: boolean | null; unlisted: boolean | null; sector: string | null; list_id: string | null; parent_task_id: string | null }[];
    const parents = new Set(rows.map((r) => r.parent_task_id).filter(Boolean) as string[]);
    const out: KyrsLeafTask[] = [];
    for (const r of rows) {
      if (r.unlisted || parents.has(r.id)) continue;
      const sector = asSector(r.sector) ?? (r.list_id ? listSector.get(r.list_id) ?? null : null);
      if (sector) out.push({ id: r.id, sector, done: !!r.done });
    }
    return out;
  })();
  inflight.clear();
  inflight.set(k, p);
  return p;
}

export function useKyrsTasks(userId: string | undefined): KyrsLeafTask[] {
  const version = useDataVersion();
  const [tasks, setTasks] = useState<KyrsLeafTask[]>([]);
  useEffect(() => {
    if (!userId) return;
    let live = true;
    load(userId, version)
      .then((r) => live && setTasks(r))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [userId, version]);
  return tasks;
}
